import { useSyncExternalStore } from 'react';
import type { PatientCatalogItem } from './types';

export interface CartLine {
  medicineId: string;
  name: string;
  unit: string;
  price: number;
  gstRate: number;
  quantity: number;
}

/**
 * One cart per pharmacy, keyed by slug — a phone that's browsed two
 * different pharmacies' storefronts (two different links) shouldn't have
 * one cart bleeding into the other.
 */
function cartKey(pharmacySlug: string) {
  return `dawa.cart.${pharmacySlug}`;
}

function readCart(pharmacySlug: string): CartLine[] {
  try {
    const raw = localStorage.getItem(cartKey(pharmacySlug));
    return raw ? (JSON.parse(raw) as CartLine[]) : [];
  } catch {
    return [];
  }
}

function writeCart(pharmacySlug: string, lines: CartLine[]) {
  try {
    localStorage.setItem(cartKey(pharmacySlug), JSON.stringify(lines));
  } catch {
    /* private mode — cart lives in memory for this tab only */
  }
  listeners.forEach((l) => l());
}

// A minimal external store: every subscribed component re-renders on any
// cart mutation, from wherever it happened (catalog grid, cart drawer, etc).
const listeners = new Set<() => void>();
function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useCart(pharmacySlug: string) {
  const lines = useSyncExternalStore(
    subscribe,
    () => readCart(pharmacySlug),
    () => [] as CartLine[],
  );

  const addItem = (medicine: PatientCatalogItem, quantity = 1) => {
    const current = readCart(pharmacySlug);
    const existing = current.find((l) => l.medicineId === medicine.medicineId);
    const next = existing
      ? current.map((l) => (l.medicineId === medicine.medicineId ? { ...l, quantity: l.quantity + quantity } : l))
      : [
          ...current,
          {
            medicineId: medicine.medicineId,
            name: medicine.name,
            unit: medicine.unit,
            price: medicine.price,
            gstRate: medicine.gstRate,
            quantity,
          },
        ];
    writeCart(pharmacySlug, next);
  };

  const setQuantity = (medicineId: string, quantity: number) => {
    const current = readCart(pharmacySlug);
    const next =
      quantity <= 0
        ? current.filter((l) => l.medicineId !== medicineId)
        : current.map((l) => (l.medicineId === medicineId ? { ...l, quantity } : l));
    writeCart(pharmacySlug, next);
  };

  const removeItem = (medicineId: string) => {
    writeCart(
      pharmacySlug,
      readCart(pharmacySlug).filter((l) => l.medicineId !== medicineId),
    );
  };

  const clear = () => writeCart(pharmacySlug, []);

  const subtotal = lines.reduce((sum, l) => sum + l.price * l.quantity, 0);
  const gstAmount = lines.reduce((sum, l) => sum + (l.price * l.quantity * l.gstRate) / 100, 0);
  const itemCount = lines.reduce((sum, l) => sum + l.quantity, 0);

  return { lines, addItem, setQuantity, removeItem, clear, subtotal, gstAmount, itemCount };
}
